import { watch } from 'vue'
import { useCorpusStore, type CorpusNode } from '../../stores/corpus'
import { syncEngine } from './syncEngine'
import { db } from '../platform/webStorage'
import { BackgroundPageCatchUp } from './backgroundPageCatchUp'
import { extractTextBlocks } from '../corpus/corpusSearch'
import { invalidateFolderOverview } from '../../composables/useFolderOverview'
import { syncMarkdownMirror } from '../corpus/corpusNodeActions'
import { SyncRequestError } from './outboxPolicy'
import { pageReadMessage, type PageReadProblem } from '../pages/pageReadMessages'

function pageIds(nodes: CorpusNode[], into = new Set<string>()): Set<string> {
  for (const node of nodes) {
    if (node.subtype === 'page' && !node.isUnsaved) into.add(node.id)
    if (node.children) pageIds(node.children, into)
  }
  return into
}

/**
 * Brings one page's local copy up to date from its edit log, then refreshes
 * what is derived from it: the page file (unless `saveFile` is false, as for
 * a trashed page), its overview and its search text. Returns its text.
 */
async function bringUpToDate(pageId: string, signal: AbortSignal, saveFile: boolean): Promise<string> {
  const corpus = useCorpusStore()
  const document = await syncEngine.catchUpPage(pageId, signal, () => corpus.activePageId === pageId)
  const content = JSON.stringify(document, null, 2)
  // The open page's file is kept by autosave.
  if (saveFile && corpus.activePageId !== pageId) await (window as any).api?.writePage?.(pageId, content)
  invalidateFolderOverview(pageId)
  if (corpus.searchTextIndex.has(pageId)) {
    corpus.searchTextIndex = new Map(corpus.searchTextIndex).set(pageId, extractTextBlocks(document as any))
  }
  return content
}

const fetching = new Map<string, Promise<string | null>>()

// Readers ask for many pages at once (a folder's previews on a new device);
// fetching them all together made the server time out on most of them.
const MAX_FETCHES = 3
let activeFetches = 0
const fetchQueue: Array<() => void> = []

async function withFetchSlot<T>(run: () => Promise<T>): Promise<T> {
  if (activeFetches >= MAX_FETCHES) await new Promise<void>((resolve) => fetchQueue.push(resolve))
  activeFetches++
  try {
    return await run()
  } finally {
    activeFetches--
    fetchQueue.shift()?.()
  }
}

/**
 * Fetches a page this device doesn't have yet, for a reader that needs it
 * now rather than when background catch-up reaches it. Null when sync is
 * unavailable, or the page's key isn't on the server yet (its device hasn't
 * uploaded it). Several readers asking at once share one fetch, and at
 * most a few pages are fetched at a time.
 */
export function fetchPageNow(pageId: string, options: { saveFile?: boolean } = {}): Promise<string | null> {
  const pending = fetching.get(pageId)
  if (pending) return pending
  const fetch = (async () => {
    if (!syncEngine.canSync.value) return null
    try {
      return await withFetchSlot(() => bringUpToDate(pageId, new AbortController().signal, options.saveFile !== false))
    } catch (err) {
      if (err instanceof SyncRequestError && err.status === 404) return null
      throw err
    }
  })().finally(() => fetching.delete(pageId))
  fetching.set(pageId, fetch)
  return fetch
}

export interface MissingPageRead {
  success: boolean
  content?: string
  /** Why it couldn't be downloaded, for a reader that words its own message. */
  problem?: PageReadProblem
  /** A message for the writer, naming the page. */
  error?: string
  /** Still connecting: the same read may work in a moment. */
  waitingSync?: boolean
}

/** Why a page can't be downloaded right now, before trying; null if it can be. */
function syncProblem(): PageReadProblem | null {
  if (syncEngine.syncLapsed.value) return 'syncLapsed'
  if (syncEngine.syncStopped.value) return 'syncStopped'
  if (!syncEngine.isOnline.value) return 'offline'
  if (!syncEngine.canSync.value) return 'connecting'
  return null
}

/**
 * Reads a page that isn't on this device by downloading it (see fetchPageNow).
 * When that can't be done, says why, in words for the writer. Both platforms'
 * readPage use this once their local copy comes up empty.
 */
export async function downloadMissingPage(pageId: string): Promise<MissingPageRead> {
  const fail = (problem: PageReadProblem): MissingPageRead => ({
    success: false,
    problem,
    error: pageReadMessage(problem, useCorpusStore().findNode(pageId)?.name || 'Untitled'),
    waitingSync: problem === 'connecting',
  })
  const problem = syncProblem()
  if (problem) return fail(problem)
  try {
    const content = await fetchPageNow(pageId)
    // Null with sync up: the page's key isn't on the server yet.
    if (content !== null) return { success: true, content }
    return fail(syncProblem() ?? 'notUploaded')
  } catch (err) {
    console.warn(`[pageCatchUp] Could not download ${pageId}:`, err)
    return fail((err as Error)?.name === 'TimeoutError' ? 'timedOut' : 'downloadFailed')
  }
}

let instance: BackgroundPageCatchUp | null = null

/** The app's page catch-up, created on first use since it reads the corpus store. */
export function pageCatchUp(): BackgroundPageCatchUp {
  if (instance) return instance
  const corpus = useCorpusStore()
  const catchUp = new BackgroundPageCatchUp({
    ready: () => syncEngine.canSync.value,
    workspaceId: () => syncEngine.ensureWorkspaceId(),
    readState: (workspaceId) => syncEngine.getPageCatchUpState(workspaceId),
    writeState: (workspaceId, state) => syncEngine.setPageCatchUpState(workspaceId, state),
    pullHeads: (workspaceId, after) => syncEngine.pullPageHeads(workspaceId, after),
    pages: () => ({
      live: pageIds(corpus.tree),
      trashed: pageIds(corpus.trash.map((entry) => entry.node)),
    }),
    isOpen: (pageId) => corpus.activePageId === pageId,
    localPosition: async (pageId) => (await db.yjsCache.get(pageId))?.serverCursor ?? null,
    catchUp: async (pageId, signal) => { await bringUpToDate(pageId, signal, true) },
    caughtUp: () => syncMarkdownMirror().catch(() => {}),
  })
  watch(() => syncEngine.canSync.value, (ready) => { if (!ready) catchUp.stop() })
  instance = catchUp
  return catchUp
}
